'use client';
import React, { useEffect, useState } from 'react';
import { AdvancedLoginIllustration } from './AdvanceLogin';

export const AnimatedLoginSequence: React.FC = () => {
  type Stage = 'inputs' | 'click' | 'lock' | 'success';
  const [stage, setStage] = useState<Stage>('inputs');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    const USER = 'john_doe@email.com';
    const PASS = '••••••';

    // timing constants (ms)
    const startDelay = 500;
    const userTypeTime = USER.length * 100 + 200;
    const passTypeTime = PASS.length * 100 + 300;
    const clickTime = 1000;
    const lockTime = 5000;
    const successHold = 2000;
    const totalCycle =
      startDelay +
      userTypeTime +
      passTypeTime +
      clickTime +
      lockTime +
      successHold;

    const runSequence = () => {
      // reset
      setStage('inputs');
      setUsername('');
      setPassword('');

      // 1) type USER
      USER.split('').forEach((ch, i) => {
        timers.push(
          setTimeout(() => setUsername((s) => s + ch), startDelay + i * 100),
        );
      });

      // 2) type PASS
      PASS.split('').forEach((ch, i) => {
        timers.push(
          setTimeout(
            () => setPassword((s) => s + ch),
            startDelay + userTypeTime + i * 100,
          ),
        );
      });

      // 3) show “Click”
      timers.push(
        setTimeout(
          () => setStage('click'),
          startDelay + userTypeTime + passTypeTime,
        ),
      );

      // 4) show “Lock”
      timers.push(
        setTimeout(
          () => setStage('lock'),
          startDelay + userTypeTime + passTypeTime + clickTime,
        ),
      );

      // 5) show “Success”
      timers.push(
        setTimeout(
          () => setStage('success'),
          startDelay + userTypeTime + passTypeTime + clickTime + lockTime,
        ),
      );

      // 6) loop again
      timers.push(setTimeout(() => runSequence(), totalCycle));
    };

    runSequence();

    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className='relative w-full h-full flex items-center justify-center'>
      <style>{ `
        @keyframes floatIn { 0% {opacity:0;transform:translateY(-30px)} 100% {opacity:1;transform:translateY(0)} }
        @keyframes fadeIn  { from {opacity:0} to {opacity:1} }
        @keyframes blink   { 0%,50%{border-color:transparent} 51%,100%{border-color:#4F46E5} }
        @keyframes handTap { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-5px) rotate(-10deg)} }
        .animate-float-in { animation: floatIn 0.8s ease-out forwards; }
        .animate-fade-in  { animation: fadeIn 0.8s ease-out forwards; }
        .typing-cursor    { border-right:2px solid #4F46E5; animation:blink 1s step-end infinite; }
        .hand             { width:32px; animation:handTap 0.6s ease-in-out infinite; }
      ` }</style>

      { /* 1) Inputs */ }
      { stage === 'inputs' && (
        <div className='flex flex-col space-y-4 p-6 bg-white rounded-xl shadow-lg animate-float-in'>
          <div className='relative'>
            <input
                className='w-64 px-4 py-2 border rounded focus:ring-2 focus:ring-indigo-400'
                placeholder='Username'
                value={username}
                readOnly
            />
            <span className='absolute right-3 top-1/2 -translate-y-1/2 typing-cursor' />
          </div>
          <div className='relative'>
            <input
                type='password'
                className='w-64 px-4 py-2 border rounded focus:ring-2 focus:ring-indigo-400'
                placeholder='Password'
                value={password}
                readOnly
            />
            <span className='absolute right-3 top-1/2 -translate-y-1/2 typing-cursor' />
          </div>
        </div>
      ) }

      { /* 2) Click */ }
      { stage === 'click' && (
        <div className='flex items-center space-x-4 animate-fade-in'>
          <button className='px-6 py-2 bg-green-900 text-white rounded shadow-lg'>
            Login
          </button>
          <svg
              className='hand'
              viewBox='0 0 64 64'
              xmlns='http://www.w3.org/2000/svg'
          >
            <path
                d='M20,10 L20,40 C20,50 28,58 38,58 48,58 56,50 56,40 56,30 48,22 38,22 34,22 31,19 31,15 31,10 25,6 20,10 Z'
                fill='#AFAFAF'
            />
          </svg>
        </div>
      ) }

      { /* 3) Lock */ }
      { stage === 'lock' && (
        <div className=' rounded-xl animate-fade-in'>
          <AdvancedLoginIllustration />
        </div>
      ) }

      { /* 4) Success */ }
      { stage === 'success' && (
        <div className='flex flex-col items-center animate-fade-in'>
          <svg
              viewBox='0 0 100 100'
              className='w-24 h-24 mb-4'
              xmlns='http://www.w3.org/2000/svg'
          >
            <circle cx='50' cy='50' r='45' fill='#10B981' />
            <path
                d='M30,52 L45,68 L70,35'
                stroke='#fff'
                strokeWidth='6'
                fill='none'
                strokeLinecap='round'
                strokeLinejoin='round'
            />
          </svg>
          <span className='text-xl font-semibold text-green-700'>
            Login Successful!
          </span>
        </div>
      ) }
    </div>
  );
};
